import { Display } from './Display';
import { $ } from '../../vendor/jquery';
import { DisplayConfiguration, ZodDisplayConfiguration } from './types';
import { setWindowVariables } from './windowVariables';
import { createZone } from '../Zone';
import { displayTemplate, getTemplateData } from './DisplayTemplate';
import { isNullish } from '../Utility/Nullish';
import { pushDisplay } from './ManageDisplays';
import { Log } from '../AuxillaryService/Log';

type LimitedDisplayConfiguration = Pick<
    DisplayConfiguration,
    'id' | 'debug' | 'width' | 'height' | 'zones'
>;

/**
 * Returns true if predicate is a non-null object which has a property
 * display_id which is either a string or number. Type guard, type narrows.
 */
function isDisplayConfigurationLike(
    predicate: unknown
): predicate is { display_id: NonNullable<string | number> } {
    if (predicate instanceof Object && null !== predicate) {
        const displayId = Reflect.get(predicate, 'display_id');
        return typeof displayId === 'string' || typeof displayId === 'number';
    }

    return false;
}

export function validateDisplayConfiguration(
    maybeDisplayConfiguration: unknown
): DisplayConfiguration {
    try {
        return ZodDisplayConfiguration.parse(maybeDisplayConfiguration);
    } catch (error) {
        const displayId = isDisplayConfigurationLike(maybeDisplayConfiguration)
            ? String(maybeDisplayConfiguration.display_id)
            : 'unknown';

        //Invalid!  Currently, we have no course correction, but at least we can be warned
        Log.Warning(
            `Display configuration (id=${displayId}) did not pass validation`,
            null,
            __fileline
        );

        return maybeDisplayConfiguration as DisplayConfiguration;
    }
}

/**
 * Create Display
 * @throws {TypeError} if missing configuration data
 */
function createDisplay(apiResults: LimitedDisplayConfiguration): Display {
    const id = apiResults.id ?? undefined;
    const width = apiResults.width ?? undefined;
    const height = apiResults.height ?? undefined;

    if (isNullish(id) || isNullish(height) || isNullish(width)) {
        const missing: string[] = [];

        if (isNullish(id)) {
            missing.push('ID');
        }
        if (isNullish(height)) {
            missing.push('Height');
        }
        if (isNullish(width)) {
            missing.push('Width');
        }

        throw new TypeError(`Missing Display configuration value(s): ${missing.join(', ')}`);
    }

    const display = new Display({
        id,
        width,
        height,
    });

    if (!isNullish(apiResults.zones)) {
        apiResults.zones.forEach((zoneData) => {
            const zone = createZone(zoneData);
            display.zones.push(zone);
        });
    }

    display.scheduleCheckLoaded();

    setWindowVariables(display);

    return display;
}

/**
 * Create Displays and prepend them to the DOM
 *
 * @param displayConfigs - Configuration data from the API used to create the Displays
 * @param displayTemplateString - Handlebars template used to build the Display/Zone elements
 * @param selector - The CSS selector denoting the element the Displays will be prepended to
 */
export function createDisplays(
    displayConfigs: Array<LimitedDisplayConfiguration>,
    selector: string
): Array<Display> {
    const displays = displayConfigs.map((displayData) => {
        return createDisplay(displayData);
    });

    // append displays to DOM
    const hbHtml = displayTemplate(getTemplateData(displays));

    $(selector).prepend(hbHtml);

    // Construct Displays/Zones after they have bee added to the DOM
    displays.forEach((display) => {
        display.construct();

        display.zones?.forEach((zone) => {
            zone.construct();
        });
    });

    pushDisplay(...displays);

    return displays;
}
